import React from 'react';
import { useTranslation } from 'react-i18next';

import { getTopKeywords } from '../utils/monitoringResultsUtils';

import type { TMonitoringResultsMode } from 'constants/interfaces/IMonitoringAgentDefinition';
import type {
  IClimateResultsSummary,
  IFinanceResultsSummary,
  IMonitoringResultsSummary,
  IOwnershipResultsSummary,
  IRegulationResultsSummary,
} from 'constants/interfaces/IMonitoringSignalResult';

import keywordStyles from './sass/keywordStyles.module.scss';
import styles from './sass/MonitoringResultsSummary.module.scss';

type TAnyResultsSummary = IMonitoringResultsSummary
| IRegulationResultsSummary
| IFinanceResultsSummary
| IOwnershipResultsSummary
| IClimateResultsSummary;

interface IMonitoringResultsSummaryProps {
  summary: TAnyResultsSummary,
  mode?: TMonitoringResultsMode,
}

const isRegulationSummary = (
  summary: TAnyResultsSummary,
): summary is IRegulationResultsSummary => (
  'total_results' in summary
);

const isOwnershipSummary = (
  summary: TAnyResultsSummary,
): summary is IOwnershipResultsSummary => (
  'total_findings' in summary && 'entities_with_active_contagion_count' in summary
);

const isFinanceSummary = (
  summary: TAnyResultsSummary,
): summary is IFinanceResultsSummary => (
  'total_findings' in summary && 'entities_monitored' in summary
);

function MonitoringResultsSummary({
  summary,
  mode = 'articles',
}: IMonitoringResultsSummaryProps): React.JSX.Element {
  const { t } = useTranslation();

  if (mode === 'financial-risk' || isFinanceSummary(summary)) {
    const financeSummary = summary as IFinanceResultsSummary;
    const themeEntries = Object.entries(financeSummary.themes_frequency || {})
      .map(([theme, count]) => ({ theme, count }))
      .sort((leftEntry, rightEntry) => rightEntry.count - leftEntry.count)
      .slice(0, 6);

    return (
      <div className={styles.container}>
        <p className={styles.summaryLine}>
          {t('valueChainMap.monitoring.results.financeSummaryLine', {
            entitiesMonitored: financeSummary.entities_monitored,
            totalFindings: financeSummary.total_findings,
            highRiskCount: financeSummary.high_risk_count,
          })}
        </p>
        <p className={styles.summaryLine}>
          {t('valueChainMap.monitoring.results.financeLiquidityFormula')}
        </p>

        {themeEntries.length ? (
          <div className={styles.keywordsSection}>
            <span className={styles.keywordsLabel}>
              {t('valueChainMap.monitoring.results.financeFindingsFeed')}
            </span>
            <div className={styles.keywordList}>
              {themeEntries.map(({ theme, count }) => (
                <span key={theme} className={styles.keywordChip}>
                  {t(`valueChainMap.monitoring.results.financeTheme.${theme}`, {
                    defaultValue: theme,
                  })}
                  <span className={styles.keywordCount}>
                    {count}
                  </span>
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (mode === 'ownership' || isOwnershipSummary(summary)) {
    const ownershipSummary = summary as IOwnershipResultsSummary;
    const themeEntries = Object.entries(ownershipSummary.themes_frequency || {})
      .map(([theme, count]) => ({ theme, count }))
      .sort((leftEntry, rightEntry) => rightEntry.count - leftEntry.count)
      .slice(0, 6);

    return (
      <div className={styles.container}>
        <p className={styles.summaryLine}>
          {t('valueChainMap.monitoring.results.ownershipSummaryLine', {
            entitiesMonitored: ownershipSummary.entities_monitored,
            totalFindings: ownershipSummary.total_findings,
            highRiskCount: ownershipSummary.high_risk_count,
          })}
        </p>

        {themeEntries.length ? (
          <div className={styles.keywordsSection}>
            <span className={styles.keywordsLabel}>
              {t('valueChainMap.monitoring.results.ownershipFindingsFeed')}
            </span>
            <div className={styles.keywordList}>
              {themeEntries.map(({ theme, count }) => (
                <span key={theme} className={styles.keywordChip}>
                  {t(`valueChainMap.monitoring.results.ownershipTheme.${theme}`, {
                    defaultValue: theme,
                  })}
                  <span className={styles.keywordCount}>
                    {count}
                  </span>
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (mode === 'climate') {
    const climateSummary = summary as IClimateResultsSummary;
    const themeEntries = Object.entries(climateSummary.themes_frequency || {})
      .map(([theme, count]) => ({ theme, count }))
      .sort((leftEntry, rightEntry) => rightEntry.count - leftEntry.count)
      .slice(0, 6);

    return (
      <div className={styles.container}>
        <p className={styles.summaryLine}>
          {t('valueChainMap.monitoring.results.climateSummaryLine', {
            entitiesMonitored: climateSummary.entities_monitored,
            totalFindings: climateSummary.total_findings,
            highRiskCount: climateSummary.high_risk_count,
          })}
        </p>

        {themeEntries.length ? (
          <div className={styles.keywordsSection}>
            <span className={styles.keywordsLabel}>
              {t('valueChainMap.monitoring.results.climateFindingsFeed')}
            </span>
            <div className={styles.keywordList}>
              {themeEntries.map(({ theme, count }) => (
                <span key={theme} className={styles.keywordChip}>
                  {t(`valueChainMap.monitoring.results.climateTheme.${theme}`, {
                    defaultValue: theme,
                  })}
                  <span className={styles.keywordCount}>
                    {count}
                  </span>
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (mode === 'regulations' || isRegulationSummary(summary)) {
    const regulationSummary = summary as IRegulationResultsSummary;

    return (
      <div className={styles.container}>
        <p className={styles.summaryLine}>
          {t('valueChainMap.monitoring.results.regulationSummaryLine', {
            totalResults: regulationSummary.total_results,
            highImpactCount: regulationSummary.high_impact_count,
            criticalImpactCount: regulationSummary.critical_impact_count,
          })}
        </p>
      </div>
    );
  }

  const articleSummary = summary as IMonitoringResultsSummary;
  const topKeywords = getTopKeywords(articleSummary.keywords_frequency);

  return (
    <div className={styles.container}>
      <p className={styles.summaryLine}>
        {t('valueChainMap.monitoring.results.summaryLine', {
          totalArticles: articleSummary.total_articles,
          highRiskCount: articleSummary.high_risk_count,
        })}
      </p>

      {topKeywords.length ? (
        <div className={keywordStyles.keywords}>
          <span className={keywordStyles.keywordsLabel}>
            {t('valueChainMap.monitoring.results.topKeywords')}
          </span>
          <div className={keywordStyles.keywordList}>
            {topKeywords.map(({ keyword, count }) => (
              <span key={keyword} className={keywordStyles.keywordChip}>
                {keyword}
                <span className={styles.keywordCount}>
                  {count}
                </span>
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

MonitoringResultsSummary.defaultProps = {
  mode: 'articles',
};

export default MonitoringResultsSummary;
